import { FC, useMemo, useEffect } from 'react'
import styles from 'components/features/external/PublicSpendingByCategory/PublicSpendingByCategory.styl'
import cn from 'classnames'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { publicFiltersSelector } from 'selectors/publicDashboardSelectors'
import { getPublicBreakdownByCategoryActionCreator } from 'actions/publicDashboardActions'
import { getItemsFromCollection, getItemFromCollection } from 'helpers/normalizeHelpers'
import { KeyValueListItem, KeyValueList } from 'components/features/pfm/KeyValueList/KeyValueList'
import { getValueDynamics } from 'helpers/publicDashboardHelpers'
import { isFetchableLoading } from 'helpers/loadableHelpers'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { metaCategoriesSelector } from 'selectors/globalSelectors'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { MOBILE_KEYVALUE_ITEMS_LIMIT } from 'constants/publicDashboard'
import { Category } from 'interfaces/meta'
import { IdValueEntity } from 'interfaces'

const SPENDING_OBJ = {
  type: 'spending' as const,
}

type PublicSpendingByCategoryProps = {

}

export const PublicSpendingByCategory: FC<PublicSpendingByCategoryProps> = (props) => {
  const {} = props

  const isMobile = useStoreSelector(isMobileSelector)
  const spendingCategories = useStoreSelector((state) => metaCategoriesSelector(state, SPENDING_OBJ))
  const { incomeRange, category, location } = useStoreSelector(publicFiltersSelector)

  const dispatch = useStoreDispatch()
  useEffect(() => {
    dispatch(getPublicBreakdownByCategoryActionCreator.request({ incomeRange, location }))
  }, [dispatch, incomeRange, location])

  const byCategoryFetchable = useStoreSelector((state) => state.data.publicdashboard.breakdownByCategory)
  const items = useMemo(() => {
    if (!byCategoryFetchable.data) {
      return null
    }

    const { total } = byCategoryFetchable.data

    const dataItems = getItemsFromCollection<IdValueEntity<string, number>, 'id'>(byCategoryFetchable.data.items)
      .reduce<KeyValueListItem[]>(
        (acc, { id, value }) => {
          const categoryObj = getItemFromCollection<Category, 'id'>(spendingCategories, id)
          const itemObj: KeyValueListItem = {
            name: categoryObj ? categoryObj.value : '',
            value: `${ Math.abs(value) }%`,
            dynamics: getValueDynamics(value),
            isHeader: category === id,
          }

          acc.push(itemObj)

          return acc
        },
        [{
          name: 'Total Spending',
          value: `${ Math.abs(total) }%`,
          dynamics: getValueDynamics(total),
          isHeader: category === 'all',
        }],
      )

    return dataItems

  }, [byCategoryFetchable, category, spendingCategories])

  const isLoading = isFetchableLoading(byCategoryFetchable)

  const preloaderChild = isLoading && (
    <LoadingOverlay />
  )

  const containerClassName = cn(styles.publicSpendingByCategory, {
    [styles.isDesktop]: !isMobile,
    [styles.noData]: (items && !items.length) || (!items && isLoading),
  })

  const limit = isMobile ? MOBILE_KEYVALUE_ITEMS_LIMIT : undefined

  return (
    <div className={ containerClassName }>
      { items && (
        <KeyValueList
          items={ items }
          isScrollable={ !isMobile }
          limit={ limit }
        />
      )}
      { preloaderChild }
    </div>
  )
}
